import React, { Component } from 'react';

import UsageLicenseTypeSelect from '~/components/legacy/UsageLicenseTypeSelect.jsx';
import TrackWaveformPlayer from '~/components/legacy/TrackWaveformPlayer.jsx';
import { buildProjectLicenseCartRequestData, buildProjectLicenseCartRemoveRequestData, buildProjectLicenseTrackingProps } from '~/components/legacy/projectLicenseCartSelection.js';
import { trackCatalogEvent, withRankingPayload } from '~/modules/catalogEventTracking.js';
import { requestJson } from '~/modules/legacyRequest.js';
import legacyEventHub from '~/modules/legacyRuntime.js';

const MIN_DEMO_DOWNLOAD_LENGTH_SECONDS = 15;

class ProjectTrack extends Component {
  constructor(props) {
    super(props);

    this.handleCartClick = this.handleCartClick.bind(this);
    this.handleLayoutDownloadClick = this.handleLayoutDownloadClick.bind(this);
    this.handleProjectTrackRemove = this.handleProjectTrackRemove.bind(this);
    this.handleProjectTrackVariantRemove = this.handleProjectTrackVariantRemove.bind(this);
    this.toggleVariants = this.toggleVariants.bind(this);
    this.handleUsageLicenseTypeSelectClose = this.handleUsageLicenseTypeSelectClose.bind(this);
    this.removeProjectLicenseSelection = this.removeProjectLicenseSelection.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.updateProjectLicenseFamily = this.updateProjectLicenseFamily.bind(this);

    this.state = {
      projectTrackRemoveBusy: false,
      projectTrackRemoveError: false,
      projectTrackVariantRemoveBusy: {},
      projectTrackVariantRemoveError: {},
      trackVariants: Array.isArray(props.track_variants) ? props.track_variants : [],
      variantsOpen: false
    };
  }

  formatLength(totalSeconds) {
    const safeSeconds = Math.max(0, Math.floor(Number(totalSeconds || 0)));
    return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, '0')}`;
  }

  cartRequestContext() {
    return withRankingPayload({ location: window.location.href }, this.props.data || this.props);
  }

  getSelectedUsageLicenseTypeIds() {
    return (this.props.cart_items || [])
      .filter((item) => item.track_id === this.props.id)
      .map((item) => item.usage_license_type_id);
  }

  getSelectedProjectLicenseFamilyKeys() {
    return (this.props.cart_items || [])
      .filter((item) => item.track_id === this.props.id && item.project_license_family_key)
      .map((item) => item.project_license_family_key);
  }

  getSelectedProjectLicenseSelections() {
    return (this.props.cart_items || [])
      .filter((item) => item.track_id === this.props.id && item.project_license_family_key)
      .map((item) => ({
        familyKey: item.project_license_family_key,
        usageLicenseTypeId: item.usage_license_type_id,
        selectedOptionKeys: item.project_license_option_keys,
        customerProjectLicensePresetId: item.customer_project_license_preset_id,
        customerEventLicensePresetId: item.customer_event_license_preset_id,
        projectLicensePresetName: item.project_license_preset_name
      }));
  }

  updateCart(usageLicenseTypeId, exposurePackId, forFx) {
    const itemId = this.props.id;
    let updateUrl;

    if (exposurePackId) {
      updateUrl = `/cart?exposure_pack_id=${exposurePackId}&usage_license_type_id=${usageLicenseTypeId}`;
    } else if (forFx) {
      updateUrl = `/cart?sound_effect_id=${itemId}&usage_license_type_id=${usageLicenseTypeId}`;
    } else {
      updateUrl = `/cart?track_id=${itemId}&usage_license_type_id=${usageLicenseTypeId}`;
    }

    return requestJson(updateUrl, {
      method: 'POST',
      data: this.cartRequestContext()
    }).then((data) => {
      this.triggerEventHub('updateCart', data);

      const cartItems = data.items || data;
      if (cartItems.some((item) => item.track_id === itemId)) {
        trackCatalogEvent('track_add_cart', { track_id: itemId, usage_license_type_id: usageLicenseTypeId, location: window.location.href });
      }
    });
  }

  updateProjectLicenseFamily(projectLicenseSelection) {
    const itemId = this.props.id;
    const requestData = buildProjectLicenseCartRequestData(itemId, projectLicenseSelection, this.cartRequestContext());
    const trackingProps = buildProjectLicenseTrackingProps(itemId, projectLicenseSelection, window.location.href);

    if (!requestData) {
      return null;
    }

    return requestJson('/cart', {
      method: 'POST',
      data: requestData
    }).then((data) => {
      this.triggerEventHub('updateCart', data);
      trackCatalogEvent('track_add_cart', trackingProps);
    });
  }

  removeProjectLicenseSelection(projectLicenseSelection) {
    const requestData = buildProjectLicenseCartRemoveRequestData(this.props.id, projectLicenseSelection);

    if (!requestData) {
      return null;
    }

    return requestJson('/cart', {
      method: 'POST',
      data: requestData
    }).then((data) => {
      this.triggerEventHub('updateCart', data);
    });
  }

  triggerEventHub(eventName, ...args) {
    if (legacyEventHub && typeof legacyEventHub.trigger === 'function') {
      legacyEventHub.trigger(eventName, ...args);
    }
  }

  handleCartClick(event) {
    event.preventDefault();
    this.props.handleCartClick(this.props.id);
  }

  handleLayoutDownloadClick(event, trackVariantId, duration) {
    event.preventDefault();

    if (!this.demoDownloadAllowed(duration)) {
      return;
    }

    const returnTo = encodeURIComponent((window.location.pathname || '/') + (window.location.search || ''));
    const customerSignedIn = (document.body?.getAttribute('data-user-tracking-context') || '').indexOf('customer') === 0;
    const link = document.createElement('a');
    const uri = trackVariantId ? `/tracks/${this.props.id}/download_layout_variant/${trackVariantId}?return_to=${returnTo}` : `/c/tracks/${this.props.id}/download_layout?return_to=${returnTo}`;
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

    link.href = uri;
    trackCatalogEvent('track_layout_download_intent', {
      track_id: this.props.id,
      track_variant_id: trackVariantId || undefined,
      location: window.location.href
    });

    if (customerSignedIn && !isChrome && !isFirefox) {
      link.setAttribute('download', '');
    }

    if (customerSignedIn) {
      trackCatalogEvent('track_layout_download', {
        track_id: this.props.id,
        track_variant_id: trackVariantId || undefined,
        location: window.location.href
      });
    }

    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  demoDownloadAllowed(duration) {
    return Number(duration || 0) >= MIN_DEMO_DOWNLOAD_LENGTH_SECONDS;
  }

  handleUsageLicenseTypeSelectClose(event) {
    event.preventDefault();

    if (typeof this.props.handleUsageLicenseTypeSelectClose === 'function') {
      this.props.handleUsageLicenseTypeSelectClose(this.props.id);
      return;
    }

    this.props.handleCartClick(this.props.id);
  }

  handleProjectTrackRemove(event) {
    event.preventDefault();

    if (this.state.projectTrackRemoveBusy || !this.props.projectTrackRemovePath) {
      return;
    }

    this.setState({ projectTrackRemoveBusy: true, projectTrackRemoveError: false });
    requestJson(this.props.projectTrackRemovePath, { method: 'DELETE' })
      .then((data) => {
        this.setState({ projectTrackRemoveBusy: false });
        this.props.onProjectTrackRemoved?.(this.props.id, data && data.project_track_count);
      })
      .catch(() => {
        this.setState({ projectTrackRemoveBusy: false, projectTrackRemoveError: true });
      });
  }

  handleProjectTrackVariantRemove(event, variant) {
    event.preventDefault();

    const removePath = variant.project_track_variant_remove_path;
    if (this.state.projectTrackVariantRemoveBusy[variant.id] || !removePath) {
      return;
    }

    this.setState((state) => ({
      projectTrackVariantRemoveBusy: { ...state.projectTrackVariantRemoveBusy, [variant.id]: true },
      projectTrackVariantRemoveError: { ...state.projectTrackVariantRemoveError, [variant.id]: false }
    }));
    requestJson(removePath, { method: 'DELETE' })
      .then(() => {
        this.setState((state) => ({
          projectTrackVariantRemoveBusy: { ...state.projectTrackVariantRemoveBusy, [variant.id]: false },
          trackVariants: state.trackVariants.filter((entry) => entry.id !== variant.id)
        }));
        this.props.onProjectTrackVariantRemoved?.(this.props.id, variant.project_track_variant_id, variant.id);
      })
      .catch(() => {
        this.setState((state) => ({
          projectTrackVariantRemoveBusy: { ...state.projectTrackVariantRemoveBusy, [variant.id]: false },
          projectTrackVariantRemoveError: { ...state.projectTrackVariantRemoveError, [variant.id]: true }
        }));
      });
  }

  renderCartAction() {
    if (this.props.freeMode) {
      return null;
    }

    const cartLabel = this.props.show_download_button ? 'Download' : (this.props.locale === 'de' ? 'Lizenz' : 'License');
    const cartActionLabel = this.props.inCart ? (this.props.remove_from_cart || cartLabel) : (this.props.add_to_cart || cartLabel);
    const icon = this.props.inCart ? (
      <span className='project-track__cart-icon project-track__cart-icon--in-cart'>
        <i className='project-track__cart-icon-added fa fa-check'></i>
        <i className='fa fa-shopping-cart project-track__cart-icon-cart'></i>
      </span>
    ) : (
      <span className='project-track__cart-icon'>
        <i className='project-track__cart-icon-add fa fa-plus'></i>
        <i className='fa fa-shopping-cart project-track__cart-icon-cart'></i>
      </span>
    );

    return (
      <a className={`project-track__cart-button${this.props.inCart ? ' project-track__cart-button--in-cart' : ''}`} href='#' onClick={this.handleCartClick} aria-label={cartActionLabel} title={cartActionLabel}>
        {icon}
        <span className='project-track__cart-label'>{cartLabel}</span>
      </a>
    );
  }

  toggleVariants() {
    this.setState((state) => ({ variantsOpen: !state.variantsOpen }));
  }

  renderDemoAction(trackVariantId, duration) {
    const disabled = !this.demoDownloadAllowed(duration);
    const label = 'Demo';
    const className = `project-track__demo${disabled ? ' project-track__demo--disabled' : ''}`;

    if (disabled) {
      return (
        <span className={className} title={this.props.locale === 'de' ? 'Demo-Download nicht verfügbar' : 'Demo download unavailable'} aria-disabled='true'>
          <i className='fa fa-download' aria-hidden='true'></i>
          <span>{label}</span>
        </span>
      );
    }

    return (
      <a className={className} href='#' onClick={(event) => this.handleLayoutDownloadClick(event, trackVariantId, duration)} title={label} aria-label={label}>
        <i className='fa fa-download' aria-hidden='true'></i>
        <span>{label}</span>
      </a>
    );
  }

  renderActions() {
    const removeLabel = this.props.locale === 'de' ? 'Aus Projekt entfernen' : 'Remove from project';
    const actionsClassName = this.props.projectTrackRemoveEnabled && this.props.projectTrackRemovePath ? 'project-track__actions' : 'project-track__actions project-track__actions--without-remove';

    return (
      <div className={actionsClassName}>
        {this.renderDemoAction(null, this.props.length)}
        {this.props.trackDetailPath ? (
          <a className='project-track__detail-link' href={this.props.trackDetailPath} aria-label={this.props.locale === 'de' ? 'Trackdetails öffnen' : 'Open track details'} title={this.props.locale === 'de' ? 'Trackdetails' : 'Track details'}>
            <i className='fa fa-external-link' aria-hidden='true'></i>
            <span>{this.props.locale === 'de' ? 'Trackdetails' : 'Track details'}</span>
          </a>
        ) : null}
        {this.props.projectTrackRemoveEnabled && this.props.projectTrackRemovePath ? (
          <button type='button' className='project-track__remove' onClick={this.handleProjectTrackRemove} disabled={this.state.projectTrackRemoveBusy} aria-label={removeLabel} title={removeLabel}>
            <i className={this.state.projectTrackRemoveBusy ? 'fa fa-spinner fa-spin' : 'fa fa-trash-o'} aria-hidden='true'></i>
            <span>{this.props.projectTrackRemoveFullLabel ? (this.props.locale === 'de' ? <>Aus Projekt<br />entfernen</> : <>Remove<br />from project</>) : (this.props.locale === 'de' ? 'Entfernen' : 'Remove')}</span>
          </button>
        ) : null}
        {this.renderCartAction()}
        {this.state.projectTrackRemoveError ? <span className='project-track__remove-error' role='alert'>{this.props.locale === 'de' ? 'Entfernen fehlgeschlagen.' : 'Could not remove.'}</span> : null}
      </div>
    );
  }

  renderLicenseSelect() {
    if (!this.props.showUsageLicenseSelect || this.props.freeMode) {
      return null;
    }

    return (
      <UsageLicenseTypeSelect
        addToCart={this.props.i18n.add_to_cart}
        allowedProjectLicenseFamilyKeys={this.props.allowedProjectLicenseFamilyKeys}
        cartItems={this.props.cart_items}
        discount={this.props.discount}
        handleCartClick={this.handleCartClick}
        i18n={this.props.i18n}
        licenseNavigatorPath={this.props.licenseNavigatorPath}
        locale={this.props.locale}
        onRequestClose={this.handleUsageLicenseTypeSelectClose}
        position='overlay'
        pricing_path={this.props.pricing_path}
        removeProjectLicenseSelection={this.removeProjectLicenseSelection}
        selectedProjectLicenseFamilyKeys={this.getSelectedProjectLicenseFamilyKeys()}
        selectedProjectLicenseSelections={this.getSelectedProjectLicenseSelections()}
        selectedUsageLicenseTypeIds={this.getSelectedUsageLicenseTypeIds()}
        selectionMode='project_license_family'
        show_download_button={this.props.show_download_button}
        subscribedUsageLicenseTypes={this.props.subscribedUsageLicenseTypes}
        trackArtist={this.props.composerName}
        trackImageMarkup={this.props.track_image_markup}
        trackTitle={this.props.display_title || this.props.title}
        updateCart={this.updateCart}
        updateProjectLicenseFamily={this.updateProjectLicenseFamily}
        usageLicenseTypes={this.props.usageLicenseTypes}
        visible={this.props.showUsageLicenseSelect}
      />
    );
  }

  renderPlayer({ className, duration, title, trackVariantId, waveformInactiveUrl, waveformPlayedUrl }) {
    const isVariant = Boolean(trackVariantId);
    const i18n = {
      pause: isVariant ? (this.props.locale === 'de' ? 'Variante pausieren' : 'Pause variant preview') : (this.props.locale === 'de' ? 'Track pausieren' : 'Pause track preview'),
      play: isVariant ? (this.props.locale === 'de' ? 'Variante abspielen' : 'Play variant preview') : (this.props.locale === 'de' ? 'Track abspielen' : 'Play track preview'),
      seek: isVariant ? (this.props.locale === 'de' ? 'In der Variante springen' : 'Seek within variant waveform') : (this.props.locale === 'de' ? 'In der Waveform springen' : 'Seek within waveform')
    };

    return (
      <TrackWaveformPlayer
        className={`project-track__player ${className || ''}`}
        durationLabel={this.formatLength(duration)}
        durationSeconds={duration}
        i18n={i18n}
        title={title}
        trackId={this.props.id}
        trackVariantId={trackVariantId}
        waveformInactiveUrl={waveformInactiveUrl}
        waveformPlayedUrl={waveformPlayedUrl}
      />
    );
  }

  renderAvailabilityNotice() {
    if (this.props.projectTrackAvailable !== false) {
      return null;
    }

    return (
      <p className='project-track__availability' role='status'>
        <i className='fa fa-info-circle' aria-hidden='true'></i>
        <span>{this.props.projectTrackAvailabilityLabel || (this.props.locale === 'de' ? 'Titel derzeit nicht verfügbar.' : 'Title currently unavailable.')}</span>
      </p>
    );
  }

  renderVariantRemoveAction(variant) {
    if (!this.props.projectTrackRemoveEnabled || !variant.project_track_variant_remove_path) {
      return null;
    }

    const removeLabel = this.props.locale === 'de' ? 'Variante aus Projekt entfernen' : 'Remove variant from project';
    const busy = this.state.projectTrackVariantRemoveBusy[variant.id];

    return (
      <button type='button' className='project-track__variant-remove' onClick={(event) => this.handleProjectTrackVariantRemove(event, variant)} disabled={busy} aria-label={removeLabel} title={removeLabel}>
        <i className={busy ? 'fa fa-spinner fa-spin' : 'fa fa-trash-o'} aria-hidden='true'></i>
        <span>{this.props.locale === 'de' ? 'Entfernen' : 'Remove'}</span>
      </button>
    );
  }

  renderVariants() {
    const variants = this.state.trackVariants;
    if (variants.length === 0) {
      return null;
    }

    const variantsId = `project-track-${this.props.id}-variants`;

    return (
      <section className='project-track__variants' aria-label={this.props.locale === 'de' ? 'Track-Varianten' : 'Track variants'}>
        <h4>
          <button type='button' className='project-track__variants-toggle' aria-controls={variantsId} aria-expanded={this.state.variantsOpen} onClick={this.toggleVariants}>
            <span>{this.props.locale === 'de' ? 'Varianten' : 'Variants'} <span className='project-track__variant-count'>({variants.length})</span></span>
            <i className={`fa fa-chevron-${this.state.variantsOpen ? 'up' : 'down'}`} aria-hidden='true'></i>
          </button>
        </h4>
        <div id={variantsId} className='project-track__variant-list' hidden={!this.state.variantsOpen}>
          {variants.map((variant) => (
            <div className='project-track__variant-row' key={`project-track-variant-${this.props.id}-${variant.id}`}>
              {this.renderPlayer({
                className: 'project-track__player--variant',
                duration: variant.length_seconds || variant.length,
                title: variant.title,
                trackVariantId: variant.id,
                waveformInactiveUrl: variant.waveform_loaded || variant.waveform_inactive,
                waveformPlayedUrl: variant.waveform_inactive || variant.waveform_played
              })}
              <div className='project-track__variant-actions'>
                {variant.project_track_variant_available === false ? <span className='project-track__variant-availability' role='status'>{variant.project_track_variant_availability_label || (this.props.locale === 'de' ? 'Nicht verfügbar' : 'Unavailable')}</span> : null}
                {this.renderDemoAction(variant.id, variant.length_seconds || variant.length)}
                {this.renderVariantRemoveAction(variant)}
                {this.state.projectTrackVariantRemoveError[variant.id] ? <span className='project-track__remove-error' role='alert'>{this.props.locale === 'de' ? 'Entfernen fehlgeschlagen.' : 'Could not remove.'}</span> : null}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  render() {
    return (
      <article className='project-track' id={`project-track-${this.props.id}`} itemScope itemType='https://schema.org/CreativeWork'>
        {this.renderAvailabilityNotice()}
        <div className='project-track__main-row'>
          {this.renderPlayer({
            className: 'project-track__player--main',
            duration: this.props.length,
            title: this.props.display_title || this.props.title,
            waveformInactiveUrl: this.props.waveform_loaded || this.props.waveform_inactive,
            waveformPlayedUrl: this.props.waveform_inactive || this.props.waveform_played
          })}
          {this.renderActions()}
        </div>
        {this.renderVariants()}
        {this.renderLicenseSelect()}
      </article>
    );
  }
}

ProjectTrack.defaultProps = {
  cart_items: [],
  freeMode: false,
  i18n: { add_to_cart: 'Lizenz' },
  locale: 'de',
  projectTrackRemoveFullLabel: true,
  projectTrackRemoveEnabled: true,
  show_download_button: false,
  subscribedUsageLicenseTypes: [],
  usageLicenseTypes: []
};

export default ProjectTrack;
